import { Head, router, useForm } from "@inertiajs/react"
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  UserMinus,
  Users,
  Search,
} from "lucide-react"
import { useState, useMemo } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

// The four assessment access levels — kept in sync with the coach portal's
// selector (App/Coach/Group/Show.jsx) and the Group#assessment_access enum.
const ACCESS_LEVEL_OPTIONS = [
  { value: "none", label: "None" },
  { value: "wheel_only", label: "Integrated Life Audit only" },
  { value: "plan_only", label: "Plan only" },
  { value: "full", label: "Full" },
]

// Inline control letting an admin set any group's assessment access level —
// admin parity with the coach portal, which only exposes this for groups the
// coach personally leads. Patches immediately on change (no separate save).
function AssessmentAccessCard({ group }) {
  return (
    <Card className="bg-white border-zinc-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-zinc-900">
          Assessment access
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-zinc-500 max-w-xl">
          Controls which assessment surfaces this group&apos;s members see. None
          shows just the group hub; Integrated Life Audit only opens the audit;
          Plan only opens the one-year plan; Full opens everything. Members
          can&apos;t tell a hidden surface exists.
        </p>
        <div className="max-w-xs">
          <Select
            value={group.assessmentAccess || "none"}
            onValueChange={(value) =>
              router.patch(
                `/app/admin/groups/${group.id}`,
                { group: { assessment_access: value } },
                { preserveScroll: true }
              )
            }
          >
            <SelectTrigger className="w-full bg-white border-zinc-300 text-zinc-900">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACCESS_LEVEL_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  )
}

// Admin-only switch (#595 follow-up). Coaches of a Certification group run
// certifying tracks from the coach portal: assign, remove, reset quiz
// attempts. Admin-only because trainees are coaches too, and a coach of a
// group that holds a certifying track sees its answer key.
function CertificationGroupCard({ group }) {
  return (
    <Card className="bg-white border-zinc-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-zinc-900">
          Certification group
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-start gap-3 text-sm text-zinc-700 cursor-pointer max-w-xl">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-kf-red focus:ring-kf-red cursor-pointer"
            checked={!!group.certificationGroup}
            onChange={(e) =>
              router.patch(
                `/app/admin/groups/${group.id}`,
                { group: { certification_group: e.target.checked } },
                { preserveScroll: true }
              )
            }
            aria-label={`Certification group: ${group.name}`}
          />
          <span>
            The coaches of this group can assign and remove certification
            tracks and reset quiz attempts in the coach portal. Put the
            people who are getting certified in as members, not coaches:
            coaches see the answer key.
          </span>
        </label>
      </CardContent>
    </Card>
  )
}

// Multi-select of member-portal sections hidden for this group's members
// (task #333) — admin parity with the coach portal, which only exposes this
// for groups the coach personally leads. A SEPARATE axis from assessment
// access (R8): checkboxes, not a <Select>, because hiding is a multi-select
// of independent tabs rather than a mutually exclusive level. Registry-driven
// (`memberSections` from the server) so the frontend never hard-codes
// section names (KTD3).
function HiddenSectionsCard({ group, memberSections }) {
  const hidden = new Set(group.hiddenMemberTabs || [])

  const toggle = (key) => {
    const next = new Set(hidden)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.add(key)
    }
    router.patch(
      `/app/admin/groups/${group.id}`,
      { group: { hidden_member_tabs: Array.from(next) } },
      { preserveScroll: true }
    )
  }

  return (
    <Card className="bg-white border-zinc-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-zinc-900">
          Hidden sections
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-zinc-500 max-w-xl">
          Removes a section from this group&apos;s members entirely — from
          their sidebar and from the page itself. Members can&apos;t tell a
          hidden section exists. Home is always visible.
        </p>
        <div className="grid grid-cols-2 gap-2 max-w-md">
          {memberSections.map((section) => (
            <label
              key={section.key}
              className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer"
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-zinc-300 text-kf-red focus:ring-kf-red cursor-pointer"
                checked={hidden.has(section.key)}
                onChange={() => toggle(section.key)}
                aria-label={`Hide ${section.label} for ${group.name}`}
              />
              {section.label}
            </label>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// Member directory switch (#651) — admin parity with the coach Group page, so
// support can turn it on for a coach. Off by default.
function MemberDirectoryCard({ group }) {
  return (
    <Card className="bg-white border-zinc-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-zinc-900">
          Member directory
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start gap-3 text-sm text-zinc-700 max-w-xl">
          {/* Same controlled, non-optimistic Switch as the coach Group page. */}
          <Switch
            className="mt-0.5"
            checked={!!group.memberDirectoryEnabled}
            onCheckedChange={(checked) =>
              router.patch(
                `/app/admin/groups/${group.id}`,
                { group: { member_directory_enabled: checked } },
                { preserveScroll: true }
              )
            }
            aria-label={`Member directory: ${group.name}`}
          />
          <span>
            Members of your other groups with the directory on can see this group&apos;s names and contact details.
          </span>
        </div>
      </CardContent>
    </Card>
  )
}

// Group replies switch (#683) — admin parity with the coach Group page. On by
// default.
function GroupRepliesCard({ group }) {
  return (
    <Card className="bg-white border-zinc-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-zinc-900">
          Group replies
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start gap-3 text-sm text-zinc-700 max-w-xl">
          {/* Same controlled, non-optimistic Switch as the coach Group page. */}
          <Switch
            className="mt-0.5"
            checked={!!group.groupRepliesEnabled}
            onCheckedChange={(checked) =>
              router.patch(
                `/app/admin/groups/${group.id}`,
                { group: { group_replies_enabled: checked } },
                { preserveScroll: true }
              )
            }
            aria-label={`Group replies: ${group.name}`}
          />
          <span>
            On, a reply to a group email (prayer request, Godly Council, or imperative) reaches the author and the whole group. Off, it reaches the author only.
          </span>
        </div>
      </CardContent>
    </Card>
  )
}

// Godly Council and Imperative email switches (#683 follow-up) — admin parity
// with the coach Group page. Both on by default.
function GroupEmailSwitchCard({ group, title, field, checked, description }) {
  return (
    <Card className="bg-white border-zinc-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-zinc-900">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start gap-3 text-sm text-zinc-700 max-w-xl">
          <Switch
            className="mt-0.5"
            checked={!!checked}
            onCheckedChange={(value) =>
              router.patch(
                `/app/admin/groups/${group.id}`,
                { group: { [field]: value } },
                { preserveScroll: true }
              )
            }
            aria-label={`${title}: ${group.name}`}
          />
          <span>{description}</span>
        </div>
      </CardContent>
    </Card>
  )
}

function EditGroupDialog({ open, onOpenChange, group }) {
  const form = useForm({
    name: group?.name || "",
    description: group?.description || "",
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    form.patch(`/app/admin/groups/${group.id}`, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              Edit Group
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Update the group details.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="edit-name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-name"
                className="bg-white border-zinc-300 text-zinc-900"
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                required
              />
              {form.errors.name && (
                <p className="text-sm text-red-600">{form.errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                className="bg-white border-zinc-300 text-zinc-900 min-h-[80px]"
                value={form.data.description}
                onChange={(e) => form.setData("description", e.target.value)}
              />
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing ? "Saving..." : "Save Changes"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

function AddMemberDialog({ open, onOpenChange, group, availableAccounts, existingMemberships }) {
  const [searchQuery, setSearchQuery] = useState("")
  const form = useForm({ account_id: "", role: "member" })

  const filteredAccounts = useMemo(() => {
    const existingIds = new Set(
      existingMemberships
        .filter((m) => m.role === form.data.role)
        .map((m) => m.account.id)
    )
    return availableAccounts.filter(
      (a) =>
        !existingIds.has(a.id) &&
        ((a.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.email.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  }, [availableAccounts, existingMemberships, form.data.role, searchQuery])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.data.account_id) return

    form.post(`/app/admin/groups/${group.id}/memberships`, {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        setSearchQuery("")
        onOpenChange(false)
      },
    })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
      setSearchQuery("")
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              Add Person to Group
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Add a person to {group.name}.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label>Role in Group</Label>
              <Select
                value={form.data.role}
                onValueChange={(value) => {
                  form.setData("role", value)
                  form.setData("account_id", "")
                }}
              >
                <SelectTrigger className="w-full bg-white border-zinc-300 text-zinc-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Person</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  type="search"
                  placeholder="Search by name or email..."
                  className="pl-9 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="max-h-48 overflow-y-auto border border-zinc-200 rounded-md">
                {filteredAccounts.length > 0 ? (
                  filteredAccounts.map((account) => (
                    <button
                      key={account.id}
                      type="button"
                      onClick={() =>
                        form.setData("account_id", account.id.toString())
                      }
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        form.data.account_id === account.id.toString()
                          ? "bg-kf-red/10 text-kf-red"
                          : "hover:bg-zinc-50 text-zinc-700"
                      }`}
                    >
                      <p className="font-medium">
                        {account.name || account.email}
                      </p>
                      {account.name && (
                        <p className="text-xs text-zinc-400">
                          {account.email}
                        </p>
                      )}
                    </button>
                  ))
                ) : (
                  <p className="px-3 py-4 text-sm text-zinc-400 text-center">
                    {searchQuery
                      ? "No matching people found"
                      : "All people are already in this group"}
                  </p>
                )}
              </div>
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing || !form.data.account_id}
            >
              {form.processing ? "Adding..." : "Add to Group"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

export default function GroupShow({
  currentUser,
  group,
  memberships,
  availableAccounts,
  pendingCount,
  memberSections = [],
}) {
  const [editOpen, setEditOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [removeMember, setRemoveMember] = useState(null)

  const coaches = memberships.filter((m) => m.role === "coach")
  const members = memberships.filter((m) => m.role === "member")

  const handleDelete = () => {
    router.delete(`/app/admin/groups/${group.id}`, {
      onSuccess: () => setDeleteOpen(false),
    })
  }

  const handleRemoveMember = () => {
    if (!removeMember) return
    router.delete(
      `/app/admin/groups/${group.id}/memberships/${removeMember.id}`,
      {
        preserveScroll: true,
        onSuccess: () => setRemoveMember(null),
      }
    )
  }

  function MemberTable({ title, items, emptyText, badgeClassName }) {
    return (
      <Card className="bg-white border-zinc-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-zinc-900">
              {title}
            </CardTitle>
            <Badge variant="outline" className={badgeClassName}>
              {items.length}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {items.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-200 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Name</TableHead>
                  <TableHead className="text-zinc-500">Email</TableHead>
                  <TableHead className="text-zinc-500 text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((membership) => (
                  <TableRow
                    key={membership.id}
                    className="border-zinc-100 hover:bg-zinc-50"
                  >
                    <TableCell className="font-medium text-zinc-900">
                      {membership.account.name || (
                        <span className="text-zinc-400 italic">No name</span>
                      )}
                    </TableCell>
                    <TableCell className="text-zinc-500">
                      {membership.account.email}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRemoveMember(membership)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        title="Remove from group"
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-zinc-400">
              <Users className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
              <p className="text-sm">{emptyText}</p>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title={`${group.name} - Kingdom Factor Admin`} />

      <div className="space-y-6">
        {/* Back + Header */}
        <div>
          <button
            onClick={() => router.visit("/app/admin/groups")}
            className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Groups
          </button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">
                {group.name}
              </h1>
              {group.description && (
                <p className="text-zinc-500 mt-1">{group.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="gradient" onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Person
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setEditOpen(true)}
                title="Edit group"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setDeleteOpen(true)}
                title="Delete group"
                className="text-red-500 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Assessment access level */}
        <AssessmentAccessCard group={group} />

        {/* Hidden member-portal sections */}
        <HiddenSectionsCard group={group} memberSections={memberSections} />

        {/* Member directory switch (#651) */}
        <MemberDirectoryCard group={group} />
        <GroupRepliesCard group={group} />
        <GroupEmailSwitchCard
          group={group}
          title="Godly Council emails"
          field="godly_council_emails_enabled"
          checked={group.godlyCouncilEmailsEnabled}
          description="On, the group gets an email when someone asks for counsel. A reply follows the Group replies setting."
        />
        <GroupEmailSwitchCard
          group={group}
          title="Imperative emails"
          field="imperative_emails_enabled"
          checked={group.imperativeEmailsEnabled}
          description="On, the group gets an email when someone makes a new imperative. A reply follows the Group replies setting."
        />

        {/* Certification group switch */}
        <CertificationGroupCard group={group} />

        {/* Coaches */}
        <MemberTable
          title="Coaches"
          items={coaches}
          emptyText="No coaches assigned yet"
          badgeClassName="border-kf-red text-kf-red"
        />

        {/* Members */}
        <MemberTable
          title="Members"
          items={members}
          emptyText="No members yet"
          badgeClassName="border-blue-500 text-blue-500"
        />
      </div>

      {/* Edit Group Dialog */}
      <EditGroupDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        group={group}
      />

      {/* Add Member Dialog */}
      <AddMemberDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        group={group}
        availableAccounts={availableAccounts}
        existingMemberships={memberships}
      />

      {/* Delete Group Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{group.name}</strong>? This
              will remove all member assignments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Member Confirmation */}
      <AlertDialog
        open={!!removeMember}
        onOpenChange={(open) => !open && setRemoveMember(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from Group</AlertDialogTitle>
            <AlertDialogDescription>
              Remove{" "}
              <strong>
                {removeMember?.account?.name || removeMember?.account?.email}
              </strong>{" "}
              from this group as {removeMember?.role}?
              {/* Task #437: an admin removing someone can silently end a
                  coach's ability to see them, and this page is the only place
                  that removal happens for coach-role memberships. */}
              {removeMember?.coachesLosingVisibility?.length > 0 && (
                <span className="mt-2 block font-medium text-red-700">
                  {removeMember.coachesLosingVisibility.join(", ")} will no longer be
                  able to see them. This is their last shared group.
                </span>
              )}
              {/* Removing a COACH is only possible here, and costs them the
                  roster they reach through this group — the case the client
                  actually hit. */}
              {removeMember?.membersLosingVisibility?.length > 0 && (
                <span className="mt-2 block font-medium text-red-700">
                  They will no longer be able to see{" "}
                  {removeMember.membersLosingVisibility.length === 1
                    ? `${removeMember.membersLosingVisibility[0]}, who shares no other group with them.`
                    : `${removeMember.membersLosingVisibility.length} of this group's members, who share no other group with them.`}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  )
}
